import { z } from 'zod';
import { OfferIdSchema } from './offer.js';
import { OfferVersionIdSchema } from './offer-version.js';
import { createPaginatedResponseSchema } from './pagination.js';
import { TagChipSchema } from './tag-definition.js';
import { filterArrayField, sharedMediaUnitFilterFields } from './media-unit-filter-fields.js';
import {
  COST_RANGE_DIMENSIONS,
  validateRangeFieldPairs,
  type CostRangeDimensionId,
} from './media-unit-cost-range-dimensions.js';

export const OFFER_UNIT_NOTE_MAX_LENGTH = 1000;

const OfferUnitIdSchema = z.number().brand<'OfferUnitId'>();

const OfferUnitDateFields = {
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
};

const OfferUnitBaseSchema = z.object({
  id: OfferUnitIdSchema,
  offerId: z.number(),
  mediaUnitId: z.number(),
  note: z.string().nullable(),
  sortOrder: z.number().int(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const OfferUnitSchema = OfferUnitBaseSchema.extend(OfferUnitDateFields);
export type OfferUnit = z.infer<typeof OfferUnitSchema>;

export const AddOfferUnitsRequestSchema = z
  .object({
    // Sanity cap raised for bulk quick-add flows; built-in Zod max needs no schema-smoke test.
    mediaUnitIds: z.array(OfferUnitSchema.shape.mediaUnitId).max(10000),
  })
  .strict();
export type AddOfferUnitsRequest = z.infer<typeof AddOfferUnitsRequestSchema>;

export const UpdateOfferUnitRequestSchema = z
  .object({
    note: z.string().max(OFFER_UNIT_NOTE_MAX_LENGTH).nullable().optional(),
    updatedAt: z.coerce.date(),
  })
  .strict();
export type UpdateOfferUnitRequest = z.infer<typeof UpdateOfferUnitRequestSchema>;

export const RemoveOfferUnitsRequestSchema = z
  .object({
    unitIds: z.array(OfferUnitIdSchema),
  })
  .strict();
export type RemoveOfferUnitsRequest = z.infer<typeof RemoveOfferUnitsRequestSchema>;

export const OfferUnitListItemSchema = OfferUnitSchema.extend({
  id: OfferUnitIdSchema,
  city: z.string(),
  address: z.string(),
  format: z.string().nullable(),
  supplier: z.string().nullable(),
  supplierNumber: z.string().nullable(),
  grwId: z.string(),
  // Server-computed, catalog-consistent display identifier — same policy as
  // the availability/pricing tabs' resolveUnitLabel. See offer-pricing.ts.
  identifier: z.string(),
  dimensions: z.string().nullable(),
  lighting: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  mediaType: z.string().nullable(),
  formatCode: z.string().nullable(),
  region: z.string().nullable(),
  county: z.string().nullable(),
  agglomeration: z.string().nullable(),
  widthM: z.number().nullable(),
  heightM: z.number().nullable(),
  dimensionNotes: z.string().nullable(),
  areaSqm: z.number().nullable(),
  audienceEstimate: z.number().nullable(),
  notes: z.string().nullable(),
  wfContactPerson: z.string().nullable(),
  locationDescription: z.string().nullable(),
  hasPhoto: z.boolean(),
  illuminated: z.boolean(),
  material: z.string().nullable(),
  multiformat: z.string().nullable(),
  // `{code, label}` chip shape, not the full TagDefinitionSchema — matches
  // unitTagsJsonExpr's projection (packages/db/.../primary-variant-media-type.ts),
  // which the offer-units list query uses for `tags`. See TagChipSchema's doc.
  tags: z.array(TagChipSchema),
  roadNumber: z.string().nullable(),
  clientDecision: z.enum(['ACCEPTED', 'REJECTED', 'UNDECIDED', 'NOT_IN_VERSION']).nullable(),
  // ─── Pricing fields (task 1.1, CU-86cbc3r8b) — `.optional()` per the
  // "Rollout and rollback" Decision (proposal.md): the API deploys before the
  // web SPA, so a revert leaves the new SPA live against the OLD api for the
  // minutes the Pages build takes. An old response simply omits these keys —
  // it predates the field — and MUST still parse in the new client. ───
  clientPriceNet: z.number().nullable().optional(),
  discountPercent: z.number().nullable().optional(),
  lineTotal: z.number().nullable().optional(),
  // Unit's stored price currency (offer_draft_unit_prices.currency) — added
  // for CU-86cbehbbv so the merged table's price-edit/bulk-discount mutations
  // can preserve a unit's actual currency instead of assuming PLN. Nullable
  // because the LEFT JOIN yields null when the unit has no price row yet;
  // `.optional()` for the same rollout/rollback reason as the sibling
  // pricing fields above (an OLD api response predates this key).
  currency: z.string().nullable().optional(),
  // ─── Cost fields (task 1.1, CU-86cbc3r8b) — names match COST_RANGE_DIMENSIONS
  // ids (media-unit-cost-range-dimensions.ts) and MediaUnitListItemSchema's LIST
  // spelling (media-units.ts) — NOT MediaUnitDetailSchema's detail-only
  // installCost/printCost spelling. Withheld (nulled) per caller RBAC at the api
  // layer (task 2.1); see costsWithheld below for the marker.
  // `.optional()` for the same rollout/rollback reason as the price fields above. ───
  catalogPrice: z.number().nullable().optional(),
  rent: z.number().nullable().optional(),
  exposureCost: z.number().nullable().optional(),
  additionalCosts: z.number().nullable().optional(),
  discountedPrice: z.number().nullable().optional(),
  lastMinutePrice: z.number().nullable().optional(),
  starterPrice: z.number().nullable().optional(),
  productionCost: z.number().nullable().optional(),
  installationCost: z.number().nullable().optional(),
  dismantlingCost: z.number().nullable().optional(),
  clientProduction: z.number().nullable().optional(),
  clientInstallation: z.number().nullable().optional(),
  clientDismantling: z.number().nullable().optional(),
  clientAdditionalFees: z.number().nullable().optional(),
  // muralEcoProduction (offer-formatka-prices) — was missing from both
  // COST_RANGE_DIMENSIONS and this schema even though `media_unit_costs.
  // mural_eco_production` has existed since the client price-breakdown
  // fields landed; closing that gap so the offer table can read it.
  muralEcoProduction: z.number().nullable().optional(),
  /**
   * True when this row's internal cost values were nulled by the server's
   * field-visibility gate rather than genuinely absent — carried on the row
   * itself, not a caller-level flag, per field-visibility's "Restricted
   * values are masked, not removed from the layout" (a caller whose grant
   * covers only some media types must be able to tell withheld from absent
   * per row). Present identically on MediaUnitListItemSchema (media-units.ts).
   *
   * Resolved INDEPENDENTLY of `supplierWithheld` below (field-visibility
   * spec: "Permission to see costs and permission to see supplier details
   * SHALL be resolved independently of one another") — a caller who may see
   * costs but not supplier details must never get this flipped true by the
   * supplier denial.
   *
   * `.default(false)` — not a bare `.optional()` — per the "Rollout and
   * rollback" Decision (proposal.md): an OLD response (pre-deploy API, during
   * the rollback window) omits this key entirely, and its absence MUST be
   * read as "not withheld" (false), never as "values were withheld". `.default()`
   * keeps the key optional on the parse INPUT (an absent key still parses)
   * while guaranteeing the parsed OUTPUT is always a real boolean, so
   * consumers never need an extra undefined-check beside the false case.
   */
  costsWithheld: z.boolean().default(false),
  /**
   * True when this row's `rent` value specifically was nulled by the
   * server's field-visibility gate (rent-cost-visibility-permission,
   * CU-86cbgnt3z) — independent of `costsWithheld` above, since a caller may
   * hold `cost.view` but lack the additional wildcard `cost.view_rent`
   * grant, in which case every other cost is visible and only rent is
   * masked. Whenever `costsWithheld` is true, this SHALL also read true, so
   * no combination of the two flags produces an unmasked-looking rent cell
   * for a caller who cannot see any of the row's costs at all. Present
   * identically on MediaUnitListItemSchema (media-units.ts).
   *
   * `.default(false)` for the same pre-deploy-response rollback-window
   * reason as `costsWithheld`.
   */
  rentWithheld: z.boolean().default(false),
  /**
   * True when this row's supplier-identifying values (`supplier`/
   * `supplierNumber`) were nulled by the server's field-visibility gate
   * rather than genuinely absent — the supplier-dimension sibling of
   * `costsWithheld` above; see that field's doc for the independence
   * requirement and the rollback-window `.default(false)` rationale. Present
   * identically on MediaUnitListItemSchema (media-units.ts) and
   * UnitDecisionsListItemSchema (offer-version-unit-decisions.ts).
   */
  supplierWithheld: z.boolean().default(false),
});
export type OfferUnitListItem = z.infer<typeof OfferUnitListItemSchema>;

/** Bare-array shape kept for the `add` response and the all-units affordance fetch. */
export const OfferUnitListResponseSchema = z.array(OfferUnitListItemSchema);
export type OfferUnitListResponse = z.infer<typeof OfferUnitListResponseSchema>;

/**
 * Pricing sort keys shared verbatim with OFFER_PRICE_SORT_KEYS (offer-pricing.ts) —
 * extracted to one source (qual-dry-violation fix) so a rename/removal of a pricing
 * sort key can't silently drift between the two independently-maintained lists.
 */
export const OFFER_UNIT_PRICING_SORT_KEYS = [
  'clientPriceNet',
  'discountPercent',
  'lineTotal',
] as const;

/**
 * Closed whitelist of sortable column keys for the offer-units list.
 * `sortOrder` is the default; never interpolated into SQL — mapped to column refs.
 */
export const OFFER_UNIT_SORT_KEYS = [
  'sortOrder',
  'mediaType',
  'format',
  'supplier',
  'supplierNumber',
  'grwId',
  'region',
  'county',
  'agglomeration',
  'city',
  'address',
  'dimensions',
  'audienceEstimate',
  'notes',
  'wfContactPerson',
  'locationDescription',
  'illuminated',
  'material',
  'multiformat',
  'tags',
  'roadNumber',
  'area',
  'note',
  'clientDecision',
  // ─── Pricing sort keys (task 1.1, CU-86cbc3r8b) — shared with OFFER_PRICE_SORT_KEYS
  // (offer-pricing.ts) via OFFER_UNIT_PRICING_SORT_KEYS above. ───
  ...OFFER_UNIT_PRICING_SORT_KEYS,
  // ─── Cost sort keys (task 1.1, CU-86cbc3r8b) — must match COST_RANGE_DIMENSIONS
  // ids (media-unit-cost-range-dimensions.ts) verbatim. A cost sort is refused at
  // the api layer (task 2.1) unless the caller's cost grant is unrestricted; this
  // enum only needs to accept the keys.
  'catalogPrice',
  'rent',
  'exposureCost',
  'additionalCosts',
  'discountedPrice',
  'lastMinutePrice',
  'starterPrice',
  'productionCost',
  'installationCost',
  'dismantlingCost',
  'clientProduction',
  'clientInstallation',
  'clientDismantling',
  'clientAdditionalFees',
  'muralEcoProduction',
] as const;
export type OfferUnitSortKey = (typeof OFFER_UNIT_SORT_KEYS)[number];

/** Coerces a URL query-string numeric range bound (e.g. "10") to a number; absent stays undefined. */
const rangeNumberParam = z.coerce.number().optional();

/**
 * Zod shape for the `<id>_min`/`<id>_max` scalar filter fields of every cost
 * dimension in COST_RANGE_DIMENSIONS — one pair of `rangeNumberParam` entries
 * per dimension, generated instead of hand-listed (mirrors the identical
 * generation in `MediaUnitFiltersSchema`, media-units.ts).
 */
type CostRangeFilterShape = {
  [K in CostRangeDimensionId as `${K}_min`]: typeof rangeNumberParam;
} & {
  [K in CostRangeDimensionId as `${K}_max`]: typeof rangeNumberParam;
};

const costRangeFilterShape: CostRangeFilterShape = Object.fromEntries(
  COST_RANGE_DIMENSIONS.flatMap((d) => [
    [`${d.id}_min`, rangeNumberParam],
    [`${d.id}_max`, rangeNumberParam],
  ]),
) as CostRangeFilterShape;

/**
 * Offer-unit filter dimensions: a subset of the shared media-unit filter fields,
 * extended with `mediaType` and `search` for filter-set parity with the catalog
 * (`MediaUnitFiltersSchema`). Matched by code/id (formatCode/supplierId), NOT by
 * display label. region/county/agglomeration/city combine into the catalog's
 * "Lokalizacja" pill (OR semantics, via the shared `buildLocationOrCondition`);
 * offer rows still carry no `lifecycleStatus`/availability/noPhoto/
 * near-POI/exclude dimensions, so those remain excluded. `.strict()` rejects
 * unsupported dimensions.
 *
 * `material`/`multiformat`/`tags`/`address` and the numeric range dimensions
 * (area/width/height/audience + every COST_RANGE_DIMENSIONS entry) round out
 * catalog parity (CU-86ca9k73a); their WHERE-clause semantics are entirely
 * owned by `buildMediaUnitFilterConditions` (packages/db), which
 * `buildOfferUnitWhere` already delegates to unconditionally.
 *
 * `status`/`notes`/`roadNumber` round out column↔filter parity
 * (catalog-column-filter-parity) — same predicates, same delegation.
 * `supplierEmail`/`supplierPhone`/`supplierContactInfo` filters were removed
 * entirely (CU-86cb1nq7h, client request — these fields belong only in the
 * Suppliers area). `supplierNumber` stays (client-confirmed key identifier).
 */
export const OfferUnitFiltersSchema = z
  .object({
    region: sharedMediaUnitFilterFields.region,
    county: sharedMediaUnitFilterFields.county,
    agglomeration: sharedMediaUnitFilterFields.agglomeration,
    city: sharedMediaUnitFilterFields.city,
    formatCode: sharedMediaUnitFilterFields.formatCode,
    mediaType: sharedMediaUnitFilterFields.mediaType,
    supplierId: sharedMediaUnitFilterFields.supplierId,
    supplierNumber: sharedMediaUnitFilterFields.supplierNumber,
    grwId: sharedMediaUnitFilterFields.grwId,
    lighting: sharedMediaUnitFilterFields.lighting,
    /** Multi-column free-text search term (catalog parity). */
    search: z.string().optional(),
    // ─── New select/text filters (catalog parity, CU-86ca9k73a) ───
    material: filterArrayField.optional(),
    multiformat: filterArrayField.optional(),
    tags: filterArrayField.optional(),
    /** Dedicated address text filter (distinct from the generic multi-column `search`). */
    address: z.string().optional(),
    // ─── Column-parity filters (catalog-column-filter-parity) ───
    status: filterArrayField.optional(),
    notes: z.string().optional(),
    roadNumber: z.string().optional(),
    // ─── Numeric range filters (catalog parity, CU-86ca9k73a) ───
    area_min: rangeNumberParam,
    area_max: rangeNumberParam,
    width_min: rangeNumberParam,
    width_max: rangeNumberParam,
    height_min: rangeNumberParam,
    height_max: rangeNumberParam,
    audience_min: rangeNumberParam,
    audience_max: rangeNumberParam,
    ...costRangeFilterShape,
  })
  .strict()
  .superRefine((value, ctx) => {
    // Numeric range pairs: a single bound is valid (one-sided range); reject
    // only an inverted range when BOTH bounds are present. Shared with
    // MediaUnitFiltersSchema's identical check (media-units.ts) via
    // validateRangeFieldPairs (media-unit-cost-range-dimensions.ts).
    validateRangeFieldPairs(value, ctx);
  });
export type OfferUnitFilters = z.infer<typeof OfferUnitFiltersSchema>;

export const OfferUnitListQuerySchema = z.object({
  /** Offset-based pagination cursor. Pass meta.nextCursor to fetch the next page. */
  cursor: z
    .string()
    .regex(/^\d+$/, 'cursor must be a non-negative integer string')
    .max(20)
    .optional(),
  pageSize: z.coerce.number().min(1).max(200).default(50),
  sortBy: z.enum(OFFER_UNIT_SORT_KEYS).default('sortOrder'),
  sortDir: z.enum(['asc', 'desc']).default('asc'),
  decisionVersionId: z.coerce.number().int().positive().pipe(OfferVersionIdSchema).optional(),
  filters: OfferUnitFiltersSchema.optional(),
});
export type OfferUnitListQuery = z.infer<typeof OfferUnitListQuerySchema>;

/** Paginated list endpoint response — replaces the bare array for `offerUnits.list`. */
export const OfferUnitListPageResponseSchema =
  createPaginatedResponseSchema(OfferUnitListItemSchema);
export type OfferUnitListPageResponse = z.infer<typeof OfferUnitListPageResponseSchema>;

/** Unpaginated/unfiltered all-units response for whole-offer affordances (map, bulk download). */
export const OfferUnitAllResponseSchema = z.array(OfferUnitListItemSchema);
export type OfferUnitAllResponse = z.infer<typeof OfferUnitAllResponseSchema>;

/** Offer-scoped filter-options: distinct filter values present among the offer's units. */
export const OfferUnitFilterOptionsResponseSchema = z.object({
  regions: z.array(z.string()),
  counties: z.array(z.string()),
  agglomerations: z.array(z.string()),
  cities: z.array(z.string()),
  formats: z.array(z.object({ code: z.string(), label: z.string() })),
  suppliers: z.array(z.object({ id: z.number(), name: z.string() })),
  supplierNumbers: z.array(z.string()),
  grwIds: z.array(z.string()),
  lightingValues: z.array(z.string()),
  /** Distinct media types (MediaTypeEnum values) present among the offer's units (catalog parity). */
  mediaTypes: z.array(z.string()),
  /** Distinct media_units.material values among the offer's units (catalog parity, CU-86ca9k73a). */
  materials: z.array(z.string()),
  /** Distinct media_units.multiformat values among the offer's units (catalog parity, CU-86ca9k73a). */
  multiformats: z.array(z.string()),
  /** Active tags used by at least one of the offer's units (catalog parity, CU-86ca9k73a). */
  tagOptions: z.array(z.object({ code: z.string(), label: z.string() })),
});
export type OfferUnitFilterOptionsResponse = z.infer<typeof OfferUnitFilterOptionsResponseSchema>;

export const AddOfferUnitsResponseSchema = z
  .object({
    units: OfferUnitListResponseSchema,
    addedCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    /**
     * Media unit ids among the units just added whose reservations
     * (SOFT_RESERVATION/HARD_RESERVATION/OPTION/SOLD) overlap the offer's
     * default campaign date range. Warn-only — the add always succeeds.
     * Omitted when the offer has no default date range set.
     */
    conflictedMediaUnitIds: z.array(z.number()).optional(),
  })
  .strict();
export type AddOfferUnitsResponse = z.infer<typeof AddOfferUnitsResponseSchema>;

export { OfferIdSchema, OfferUnitIdSchema };
